import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsService } from '../../services/analyticsService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [storePerformance, setStorePerformance] = useState([]);
  const [engagementRate, setEngagementRate] = useState(null);
  const [engagementTrends, setEngagementTrends] = useState([]);
  const [promotionPerformance, setPromotionPerformance] = useState([]);
  const [realTimeKPIs, setRealTimeKPIs] = useState(null);
  const [zonePerformance, setZonePerformance] = useState([]);
  const [trendDays, setTrendDays] = useState(30);

  const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739'];

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (userRole !== 'admin') {
      navigate('/');
      return;
    }

    loadAnalyticsData();
  }, [user, userRole, navigate]);

  useEffect(() => {
    if (user && userRole === 'admin') {
      loadEngagementTrends();
    }
  }, [trendDays, user, userRole]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError('');
      const [
        summaryData, 
        categoriesData, 
        performanceData, 
        engagementData,
        trendsData,
        promoData,
        kpiData,
        zoneData
      ] = await Promise.all([
        analyticsService?.getAnalyticsSummary(),
        analyticsService?.getPopularCategories(),
        analyticsService?.getStorePerformance(),
        analyticsService?.getPromotionEngagementRate(),
        analyticsService?.getEngagementTrends(trendDays),
        analyticsService?.getPromotionPerformance(),
        analyticsService?.getRealTimeKPIs(),
        analyticsService?.getZonePerformance()
      ]);
      setSummary(summaryData);
      setCategories(categoriesData);
      setStorePerformance(performanceData);
      setEngagementRate(engagementData);
      setEngagementTrends(trendsData);
      setPromotionPerformance(promoData);
      setRealTimeKPIs(kpiData);
      setZonePerformance(zoneData);
    } catch (err) {
      setError(err?.message || 'Errore nel caricamento dei dati analytics');
    } finally {
      setLoading(false);
    }
  };

  const loadEngagementTrends = async () => {
    try {
      const trendsData = await analyticsService?.getEngagementTrends(trendDays);
      setEngagementTrends(trendsData);
    } catch (err) {
      console.error('Error loading trends:', err);
    }
  };

  const getCategoryLabel = (value) => {
    const labels = {
      'bar-caffe': 'Bar & Caffè',
      'ristoranti-pizzerie': 'Ristoranti & Pizzerie',
      'abbigliamento-moda': 'Abbigliamento & Moda',
      'bellezza-benessere': 'Bellezza & Benessere',
      'casa-arredamento': 'Casa & Arredamento',
      'elettronica-tecnologia': 'Elettronica & Tecnologia',
      'salute-farmacia': 'Salute & Farmacia',
      'sport-tempo-libero': 'Sport & Tempo Libero',
      'servizi-professionali': 'Servizi Professionali'
    };
    return labels?.[value] || value;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date?.getDate()}/${date?.getMonth() + 1}`;
  };

  const getChangeIcon = (change) => {
    const changeNum = parseFloat(change);
    if (changeNum > 0) return <Icon name="TrendingUp" size={16} className="text-success" />;
    if (changeNum < 0) return <Icon name="TrendingDown" size={16} className="text-destructive" />;
    return <Icon name="Minus" size={16} className="text-muted-foreground" />;
  };

  const getChangeColor = (change) => {
    const changeNum = parseFloat(change);
    if (changeNum > 0) return 'text-success';
    if (changeNum < 0) return 'text-destructive';
    return 'text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 pb-12 px-6">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Analytics Dashboard Consolidato - Admin | CLICCAMI.NET</title>
      </Helmet>
      <Header />
      <div className="pt-24 pb-12 px-6">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-heading font-bold text-foreground mb-2">Analytics Dashboard Consolidato</h1>
              <p className="text-muted-foreground">Vista unificata GA4 + Supabase con KPI in tempo reale e trend di engagement</p>
            </div>
            <Button
              variant="outline"
              iconName="RefreshCw"
              onClick={loadAnalyticsData}
              disabled={loading}
            >
              Aggiorna
            </Button>
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">Errore</p>
                  <p className="text-sm text-muted-foreground">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Real-Time KPIs Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Activity" size={24} className="text-primary" />
              KPI in Tempo Reale (Ultime 24h)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Eye" size={24} className="text-primary" />
                  </div>
                  <div className="flex items-center gap-1">
                    {getChangeIcon(realTimeKPIs?.views?.change)}
                    <span className={`text-sm font-semibold ${getChangeColor(realTimeKPIs?.views?.change)}`}>
                      {realTimeKPIs?.views?.change}%
                    </span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-1">Visualizzazioni</p>
                <p className="text-3xl font-bold text-foreground">{realTimeKPIs?.views?.current || 0}</p>
                <p className="text-xs text-muted-foreground mt-2">vs {realTimeKPIs?.views?.previous || 0} (24h precedenti)</p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="Bookmark" size={24} className="text-success" />
                  </div>
                  <div className="flex items-center gap-1">
                    {getChangeIcon(realTimeKPIs?.saves?.change)}
                    <span className={`text-sm font-semibold ${getChangeColor(realTimeKPIs?.saves?.change)}`}>
                      {realTimeKPIs?.saves?.change}%
                    </span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-1">Salvataggi</p>
                <p className="text-3xl font-bold text-foreground">{realTimeKPIs?.saves?.current || 0}</p>
                <p className="text-xs text-muted-foreground mt-2">vs {realTimeKPIs?.saves?.previous || 0} (24h precedenti)</p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Icon name="MousePointerClick" size={24} className="text-warning" />
                  </div>
                  <div className="flex items-center gap-1">
                    {getChangeIcon(realTimeKPIs?.engagements?.change)}
                    <span className={`text-sm font-semibold ${getChangeColor(realTimeKPIs?.engagements?.change)}`}>
                      {realTimeKPIs?.engagements?.change}%
                    </span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-1">Engagement Promozioni</p>
                <p className="text-3xl font-bold text-foreground">{realTimeKPIs?.engagements?.current || 0}</p>
                <p className="text-xs text-muted-foreground mt-2">vs {realTimeKPIs?.engagements?.previous || 0} (24h precedenti)</p>
              </div>
            </div>
          </div>

          {/* Engagement Trends Chart */}
          <div className="bg-card rounded-xl p-6 border border-border shadow-warm mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Icon name="TrendingUp" size={24} className="text-primary" />
                Trend di Engagement
              </h2>
              <div className="flex gap-2">
                <Button
                  variant={trendDays === 7 ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTrendDays(7)}
                >
                  7 giorni
                </Button>
                <Button
                  variant={trendDays === 30 ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTrendDays(30)}
                >
                  30 giorni
                </Button>
                <Button
                  variant={trendDays === 90 ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTrendDays(90)}
                >
                  90 giorni
                </Button>
              </div>
            </div>
            {engagementTrends?.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={engagementTrends}>
                  <defs>
                    <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FF6B6B" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#FF6B6B" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorSaves" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4ECDC4" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#4ECDC4" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorEngagements" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FFA07A" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#FFA07A" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={formatDate}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    labelFormatter={(value) => `Data: ${formatDate(value)}`}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#FF6B6B" 
                    fillOpacity={1} 
                    fill="url(#colorViews)" 
                    name="Visualizzazioni"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="saves" 
                    stroke="#4ECDC4" 
                    fillOpacity={1} 
                    fill="url(#colorSaves)" 
                    name="Salvataggi"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="engagements" 
                    stroke="#FFA07A" 
                    fillOpacity={1} 
                    fill="url(#colorEngagements)" 
                    name="Engagement Promo"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-muted-foreground">
                <p>Nessun dato disponibile per il periodo selezionato</p>
              </div>
            )}
          </div>

          {/* Summary Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Eye" size={24} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Visualizzazioni Totali</p>
                  <p className="text-3xl font-bold text-foreground">{summary?.totalViews || 0}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">Media: {summary?.avgViewsPerStore || 0} per vetrina</p>
            </div>

            <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="Bookmark" size={24} className="text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Salvataggi Totali</p>
                  <p className="text-3xl font-bold text-foreground">{summary?.totalSaves || 0}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">Media: {summary?.avgSavesPerStore || 0} per vetrina</p>
            </div>

            <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Tag" size={24} className="text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Promozioni Attive</p>
                  <p className="text-3xl font-bold text-foreground">{summary?.totalPromotions || 0}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">Engagement: {engagementRate?.totalEngagements || 0} interazioni</p>
            </div>

            <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-info/10 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} className="text-info" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tasso di Engagement</p>
                  <p className="text-3xl font-bold text-foreground">{engagementRate?.engagementRate || 0}%</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">Promozioni vs Interazioni</p>
            </div>
          </div>

          {/* Promotion Performance Table */}
          <div className="bg-card rounded-xl p-6 border border-border shadow-warm mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Icon name="Tag" size={24} className="text-primary" />
              Performance Promozioni
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Promozione</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Negozio</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Visualizzazioni</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Click</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">CTR</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Conversioni</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Conv. Rate</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Condivisioni</th>
                  </tr>
                </thead>
                <tbody>
                  {promotionPerformance?.slice(0, 10)?.map((promo, index) => (
                    <tr key={promo?.id} className="border-b border-border hover:bg-accent/50 transition-smooth">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {index < 3 && (
                            <Icon 
                              name="Award" 
                              size={16} 
                              className={index === 0 ? 'text-warning' : index === 1 ? 'text-muted-foreground' : 'text-orange-600'} 
                            />
                          )}
                          <span className="text-sm font-medium text-foreground">{promo?.titolo}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{promo?.storeName}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-semibold">
                          <Icon name="Eye" size={12} />
                          {promo?.viewCount}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-info/10 text-info rounded-full text-xs font-semibold">
                          <Icon name="MousePointerClick" size={12} />
                          {promo?.clickCount}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-sm font-semibold text-foreground">{promo?.ctr}%</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded-full text-xs font-semibold">
                          <Icon name="CheckCircle" size={12} />
                          {promo?.conversionCount}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-sm font-semibold text-foreground">{promo?.conversionRate}%</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning rounded-full text-xs font-semibold">
                          <Icon name="Share2" size={12} />
                          {promo?.shareCount}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {promotionPerformance?.length === 0 && (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <p>Nessuna promozione attiva trovata</p>
                </div>
              )}
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Popular Categories Chart */}
            <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                <Icon name="BarChart3" size={24} className="text-primary" />
                Categorie Popolari
              </h2>
              {categories?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={categories?.slice(0, 6)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="categoria" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={getCategoryLabel}
                      angle={-45}
                      textAnchor="end"
                      height={100}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      labelFormatter={getCategoryLabel}
                    />
                    <Legend />
                    <Bar dataKey="store_count" fill="#FF6B6B" name="Vetrine" />
                    <Bar dataKey="total_views" fill="#4ECDC4" name="Visualizzazioni" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <p>Nessun dato disponibile</p>
                </div>
              )}
            </div>

            {/* Zone Performance Chart */}
            <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                <Icon name="MapPin" size={24} className="text-primary" />
                Performance per Zona
              </h2>
              {zonePerformance?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={zonePerformance}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="zona" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar dataKey="viewCount" fill="#45B7D1" name="Visualizzazioni" />
                    <Bar dataKey="saveCount" fill="#98D8C8" name="Salvataggi" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <p>Nessun dato disponibile</p>
                </div>
              )}
            </div>
          </div>

          {/* GA4 Integration Info */}
          <div className="bg-gradient-to-r from-primary/10 to-info/10 border border-primary/20 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="BarChart2" size={24} className="text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-foreground mb-2">Google Analytics 4 - Analisi Comportamentale</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  I dati Supabase qui mostrati sono integrati con GA4 per tracciamento comportamentale completo. 
                  Eventi tracciati in tempo reale:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm text-foreground">Click e navigazione vetrine</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm text-foreground">Engagement promozioni</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm text-foreground">Utilizzo filtri e ricerche</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm text-foreground">Pattern di scoperta</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm text-foreground">Salvataggi e preferenze</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm text-foreground">Tempo di permanenza</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-primary/20">
                  <a 
                    href="https://analytics.google.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-semibold text-sm transition-smooth"
                  >
                    <Icon name="ExternalLink" size={16} />
                    Apri Google Analytics Dashboard per analisi dettagliate
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Top Performing Stores */}
          <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Icon name="Award" size={24} className="text-primary" />
              Top Performance Vetrine
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Negozio</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Categoria</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Zona</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Visualizzazioni</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Salvataggi</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Promo</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Engagement</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">In Evidenza</th>
                  </tr>
                </thead>
                <tbody>
                  {storePerformance?.slice(0, 20)?.map((store, index) => (
                    <tr key={store?.storeId} className="border-b border-border hover:bg-accent/50 transition-smooth">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {index < 3 && (
                            <Icon 
                              name="Award" 
                              size={16} 
                              className={index === 0 ? 'text-warning' : index === 1 ? 'text-muted-foreground' : 'text-orange-600'} 
                            />
                          )}
                          <span className="text-sm font-medium text-foreground">{store?.nomeNegozio}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{getCategoryLabel(store?.categoria)}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{store?.zona}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-semibold">
                          <Icon name="Eye" size={12} />
                          {store?.viewCount}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded-full text-xs font-semibold">
                          <Icon name="Bookmark" size={12} />
                          {store?.saveCount}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {store?.hasPromotion ? (
                          <Icon name="Check" size={16} className="text-success mx-auto" />
                        ) : (
                          <Icon name="X" size={16} className="text-muted-foreground mx-auto" />
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning rounded-full text-xs font-semibold">
                          <Icon name="MousePointerClick" size={12} />
                          {store?.promotionEngagement}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {store?.inEvidenza ? (
                          <Icon name="Star" size={16} className="text-warning mx-auto" />
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {storePerformance?.length === 0 && (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <p>Nessuna vetrina trovata</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;